import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppBar, Toolbar, Button, Box, Typography, Drawer, IconButton, List, ListItem, ListItemButton, ListItemText } from '@mui/material';
import HikingIcon from '@mui/icons-material/Hiking';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const navItems = [
    { text: 'Home', path: '/' },
    { text: 'About', path: '/about' },
    { text: 'TX State Parks', path: '/tx-state-parks' },
    { text: 'National Parks', path: '/national-parks' },
    { text: 'Checklist', path: '/checklist' }
  ];

  const drawerContent = (
    <Box sx={{ width: 250, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={handleDrawerToggle}>
          <CloseIcon sx={{ color: '#2d5016' }} />
        </IconButton>
      </Box>
      <List>
        {navItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.path}
              onClick={handleDrawerToggle}
              sx={{
                borderRadius: '8px',
                mb: 1,
                '&:hover': {
                  backgroundColor: 'rgba(77, 182, 172, 0.1)',
                },
              }}
            >
              <ListItemText primary={item.text} sx={{ color: '#2c3e50', fontWeight: 600 }} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <AppBar
      position="static"
      elevation={4}
      sx={{
        background: 'linear-gradient(135deg, #2d5016 0%, #4a7c59 50%, #8b7355 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
        borderBottom: '2px solid #4ade80',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          opacity: 0.1,
        },
      }}
    >
      <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', px: { xs: 1.5, sm: 4 }, py: { xs: 0.5, sm: 1 }, minHeight: { xs: 56, sm: 64 } }}>
        
        {/* Logo & Title */}
        <Box
          component={Link}
          to="/"
          sx={{
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            color: 'white',
            transition: 'all 0.3s ease',
            minWidth: 0,
            '&:hover': {
              transform: 'scale(1.05)',
              textShadow: '0 0 10px rgba(255,255,255,0.5)',
            },
          }}
        >
          <HikingIcon sx={{ mr: { xs: 1, sm: 1.5 }, fontSize: { xs: 28, sm: 36 }, color: '#ffd700', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 800, 
                letterSpacing: '-0.5px', 
                lineHeight: 1.1,
                fontSize: { xs: '0.875rem', sm: '1.25rem' },
                whiteSpace: 'nowrap',
              }}
            >
              TX Park Hoppers
            </Typography>
          </Box>
        </Box>

        {/* Desktop Navigation */}
        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
          {navItems.map((item) => (
            <Button
              key={item.text}
              component={Link}
              to={item.path}
              sx={{
                color: 'white',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { sm: '0.9rem', md: '1rem' },
                px: { sm: 1, md: 2 },
                py: 1,
                borderRadius: '20px',
                position: 'relative',
                transition: 'all 0.3s ease',
                backgroundColor: 'rgba(255,255,255,0.1)',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  width: 0,
                  height: '2px',
                  backgroundColor: '#ffd700',
                  transition: 'all 0.3s ease',
                  transform: 'translateX(-50%)',
                },
                '&:hover::after': {
                  width: '80%',
                },
              }}
            >
              {item.text}
            </Button>
          ))}
        </Box>

        {/* Mobile Menu Icon */}
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            display: { md: 'none' },
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      {/* Mobile Drawer */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{
          display: { md: 'none' },
        }}
      >
        {drawerContent}
      </Drawer>
    </AppBar>
  );
}

export default Header;